import { useState, useRef, useEffect } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { HardDrive, Lock, Download, FolderDown, Loader2, ShieldCheck, RefreshCw, Check, AlertTriangle, RotateCcw } from "lucide-react";

const supportsFS = typeof window !== "undefined" && "showDirectoryPicker" in window;

function buildFilename() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `erbegroup-backup-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}.json`;
}
const fmtTime = (d) => (d ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—");

export default function Hd() {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirName, setDirName] = useState(null);
  const [auto, setAuto] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const dirHandle = useRef(null);
  const pwRef = useRef("");
  const autoTimer = useRef(null);

  const unlock = async (e) => {
    e?.preventDefault();
    if (!password) { toast.error("Inserisci la password amministratore"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/backup/verify", { password });
      setUnlocked(true); setCounts(data.counts); pwRef.current = password;
      toast.success("Accesso consentito alla sezione HD");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const fetchDump = async () => {
    const { data } = await api.post("/backup/export", { password: pwRef.current });
    return data;
  };

  const download = async () => {
    setBusy(true);
    try {
      const dump = await fetchDump();
      const blob = new Blob([JSON.stringify(dump, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = buildFilename(); a.click();
      URL.revokeObjectURL(url);
      setLastBackup(new Date());
      toast.success(`Backup scaricato (${dump.totale_record} record)`);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const writeToDir = async () => {
    const dump = await fetchDump();
    const fh = await dirHandle.current.getFileHandle(buildFilename(), { create: true });
    const w = await fh.createWritable();
    await w.write(JSON.stringify(dump, null, 2));
    await w.close();
    setLastBackup(new Date());
    return dump;
  };

  const chooseDir = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: "readwrite", id: "erbe-backup" });
      dirHandle.current = handle; setDirName(handle.name);
      setBusy(true);
      const dump = await writeToDir();
      toast.success(`Backup salvato su "${handle.name}" (${dump.totale_record} record)`);
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Impossibile accedere al disco esterno");
    }
    setBusy(false);
  };

  const backupNow = async () => {
    if (!dirHandle.current) { toast.error("Prima scegli la cartella del disco esterno"); return; }
    setBusy(true);
    try { const d = await writeToDir(); toast.success(`Backup eseguito (${d.totale_record} record)`); }
    catch { toast.error("Backup su disco non riuscito"); }
    setBusy(false);
  };

  const restore = async (file) => {
    if (!file) return;
    if (!window.confirm("ATTENZIONE: il ripristino SOSTITUISCE tutti i dati attuali con quelli del file di backup. Questa operazione non è reversibile. Continuare?")) return;
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const { data } = await api.post("/backup/restore", { password: pwRef.current, data: parsed });
      toast.success(`Ripristino completato: ${data.totale_record} record. Ricarico la pagina…`);
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      toast.error(err?.name === "SyntaxError" ? "File di backup non valido" : apiError(err));
    }
    setBusy(false);
  };

  useEffect(() => {
    if (auto && dirHandle.current) {
      writeToDir().catch(() => {});
      autoTimer.current = setInterval(() => { writeToDir().catch(() => {}); }, 30 * 60 * 1000);
    }
    return () => { if (autoTimer.current) { clearInterval(autoTimer.current); autoTimer.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto]);

  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-primary";

  if (!unlocked) {
    return (
      <div className="animate-fade-up max-w-md mx-auto mt-10">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-md bg-primary/15 flex items-center justify-center"><HardDrive className="w-6 h-6 text-primary" /></div>
          <h1 className="font-display font-black text-3xl text-slate-50 tracking-tight">HD / Backup</h1>
        </div>
        <p className="text-slate-400 mb-6">Sezione riservata. Inserisci la password amministratore per accedere ai backup dei dati.</p>
        <form onSubmit={unlock} className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Lock className="w-4 h-4" /> Accesso protetto</div>
          <input data-testid="hd-password" type="password" autoFocus placeholder="Password amministratore" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
          <button data-testid="hd-unlock" type="submit" disabled={busy} className="w-full bg-primary text-slate-950 font-semibold rounded-md py-2.5 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Sblocca
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="animate-fade-up" data-testid="hd-panel">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-md bg-primary/15 flex items-center justify-center"><HardDrive className="w-6 h-6 text-primary" /></div>
        <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight">HD / Backup</h1>
      </div>
      <p className="text-slate-400 mb-6">Scarica una copia completa dei tuoi dati o salvala direttamente su un disco esterno collegato al PC.</p>

      {counts && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="hd-counts">
          {Object.entries(counts).map(([k, v]) => (
            <span key={k} className="text-xs bg-slate-800/70 border border-slate-700 rounded-md px-2.5 py-1 text-slate-300">
              {k}: <span className="font-mono-plex text-slate-100">{v}</span>
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Scarica dati */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2"><Download className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-slate-100">Scarica dati</h2></div>
          <p className="text-sm text-slate-400 mb-4">Scarica un file di backup completo (JSON) nella cartella Download del PC. Funziona su qualsiasi browser. Sposta poi il file sul tuo disco esterno.</p>
          <button data-testid="hd-download" onClick={download} disabled={busy} className="w-full bg-primary text-slate-950 font-semibold rounded-md py-2.5 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Scarica backup ora
          </button>
        </div>

        {/* Backup su disco esterno */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2"><FolderDown className="w-5 h-5 text-primary" /><h2 className="font-display font-bold text-slate-100">Backup su disco esterno</h2></div>
          {supportsFS ? (
            <>
              <p className="text-sm text-slate-400 mb-4">Collega l'hard disk al PC, scegli la cartella una volta e i backup verranno scritti direttamente lì. Con il backup automatico attivo, salva una copia ogni 30 minuti finché questa pagina resta aperta.</p>
              <button data-testid="hd-choose-dir" onClick={chooseDir} disabled={busy} className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 font-semibold rounded-md py-2.5 flex items-center justify-center gap-2 transition-colors disabled:opacity-60 mb-3">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderDown className="w-4 h-4" />} {dirName ? `Cartella: ${dirName}` : "Scegli cartella disco esterno"}
              </button>
              {dirName && (
                <div className="space-y-3">
                  <button data-testid="hd-backup-now" onClick={backupNow} disabled={busy} className="w-full bg-primary text-slate-950 font-semibold rounded-md py-2.5 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                    <RefreshCw className="w-4 h-4" /> Esegui backup adesso
                  </button>
                  <label data-testid="hd-auto-toggle" className="flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded-md px-3 py-2.5 cursor-pointer">
                    <span className="text-sm text-slate-200">Backup automatico ogni 30 min</span>
                    <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="w-4 h-4 accent-emerald-500" />
                  </label>
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    {auto ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : null}
                    Ultimo backup su disco: {fmtTime(lastBackup)}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-amber-300 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Il tuo browser non supporta la scrittura diretta su disco. Usa <b>Google Chrome</b> o <b>Microsoft Edge</b> su PC, oppure usa "Scarica dati" e sposta il file sul disco esterno.
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-600 mt-6">Il file di backup contiene tutti i dati dell'app (bolle, documenti, acquisti, consegne, resi, utenti, impostazioni e apprendimento). Conservalo in un luogo sicuro.</p>

      {/* Ripristino da backup */}
      <div className="bg-slate-900/50 border border-red-500/30 rounded-lg p-6 mt-6">
        <div className="flex items-center gap-2 mb-2"><RotateCcw className="w-5 h-5 text-red-400" /><h2 className="font-display font-bold text-slate-100">Ripristina da backup</h2></div>
        <div className="text-sm text-amber-300 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-md p-3 mb-4">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Attenzione: il ripristino <b>sostituisce tutti i dati attuali</b> con quelli del file selezionato. Usalo solo dopo un errore o su un nuovo PC.
        </div>
        <label data-testid="hd-restore-label" className="inline-flex items-center gap-2 cursor-pointer bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 font-semibold rounded-md px-4 py-2.5 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Seleziona file di backup (.json)
          <input data-testid="hd-restore-input" type="file" accept="application/json,.json" className="hidden" onChange={(e) => restore(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
}
